import { useEffect, useRef, useState } from 'react'
import { Card, CardContent } from './ui/card'
import { Badge } from './ui/badge'
import {
  Target,
  Eye,
  Heart,
  Award,
  Users,
  TrendingUp,
  CheckCircle,
  Star
} from 'lucide-react'

/** Sayım animasyonu için küçük hook */
function useCountUp(target, inView, duration = 1200) {
  const [val, setVal] = useState(0)
  useEffect(() => {
    if (!inView) return
    const start = performance.now()
    const from = 0
    const to = target
    let raf
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration)
      setVal(Math.round(from + (to - from) * p))
      if (p < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [inView, target, duration])
  return val
}

export default function About() {
  // Gözlem (ekranda görünür olunca animasyon başlasın)
  const statsRef = useRef(null)
  const [statsInView, setStatsInView] = useState(false)
  useEffect(() => {
    const io = new IntersectionObserver(
      ([e]) => setStatsInView(e.isIntersecting),
      { threshold: 0.35 }
    )
    if (statsRef.current) io.observe(statsRef.current)
    return () => io.disconnect()
  }, [])

  const stats = [
    { number: 15, suffix: '+', label: 'Yıl Tecrübe', icon: Award },
    { number: 500, suffix: '+', label: 'Mutlu Müşteri', icon: Users },
    { number: 1000, suffix: '+', label: 'Tamamlanan İşlem', icon: TrendingUp },
    { number: 99, suffix: '%', label: 'Memnuniyet', icon: Star },
  ]

  const values = [
    {
      icon: Target,
      title: 'Misyonumuz',
      description:
        'Antrepo gümrük işlemlerinde hızlı, doğru ve güvenilir hizmet sunmak.',
      color: 'blue',
    },
    {
      icon: Eye,
      title: 'Vizyonumuz',
      description:
        'Teknoloji ve inovasyonla sektörde kalıcı liderlik.',
      color: 'green',
    },
    {
      icon: Heart,
      title: 'Değerlerimiz',
      description:
        'Güvenilirlik, şeffaflık, müşteri odaklılık ve sürekli gelişim.',
      color: 'purple',
    },
  ]

  const features = [
    'Lisanslı ve sertifikalı gümrük müşavirleri',
    'Rekabetçi ve öngörülebilir fiyatlandırma',
    'SLA ile tanımlı hızlı işlem akışları',
    'Canlı durum takibi ve düzenli raporlama',
    'Mevzuat uyumluluğu ve risk azaltma',
    'Uçtan uca danışmanlık ve destek',
  ]

  const colorDot = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
  }

  return (
    <section id="about" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Başlık */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Hakkımızda</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            15+ yıllık deneyimle antrepo gümrük işlemlerinde güvenilir çözüm ortağınız.
          </p>
        </div>

        {/* İstatistikler - sabit */}
<div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
  {stats.map(({ number, suffix, label, icon: Icon }, i) => (
    <Card
      key={i}
      className="p-6 text-center border border-gray-200 hover:shadow-lg transition-all"
    >
      <CardContent className="p-0 flex flex-col items-center">
        <div className="w-16 h-16 rounded-full bg-orange-50 text-orange-600 flex items-center justify-center mb-4">
          <Icon size={24} />
        </div>
        <div className="text-3xl font-extrabold text-gray-900 mb-1 tabular-nums">
          {number}{suffix}
        </div>
        <div className="text-gray-600">{label}</div>
      </CardContent>
    </Card>
  ))}
</div>


        {/* Güvenilir Gümrük Ortağınız — yeniden tasarım */}
        <div className="grid lg:grid-cols-2 gap-10 items-start mb-16">
          <div className="space-y-5">
            <Badge className="mb-1">Şirket Hikayemiz</Badge>
            <h3 className="text-3xl font-bold text-gray-900">
              <span className="border-l-4 border-orange-500 pl-3">
                Güvenilir Gümrük Ortağınız
              </span>
            </h3>
            <p className="text-gray-600 leading-relaxed">
              Antrepo gümrük işlemlerinde uzman ekibimizle süreçlerinizi hızlandırır,
              şeffaflık ve doğrulukla ilerletiriz. Teknoloji odaklı yaklaşımımızla
              her dosyada ölçülebilir kalite ve izlenebilirlik sağlarız.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {['Şeffaf süreç', 'Hızlı sonuç', 'Mevzuat uyumu', 'Ölçülebilir kalite'].map((t) => (
                <div key={t} className="flex items-center gap-2">
                  <CheckCircle className="text-green-500" size={18} />
                  <span className="text-gray-700">{t}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Misyon/Vizyon/Değerler kartları */}
          <div className="grid sm:grid-cols-3 gap-4">
            {values.map(({ icon: Icon, title, description, color }, i) => (
              <Card key={i} className="p-5 hover:shadow-lg transition-all border border-gray-200">
                <CardContent className="p-0">
                  <div
                    className={`w-10 h-10 rounded-lg ${colorDot[color]} flex items-center justify-center mb-3`}
                  >
                    <Icon size={20} />
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">{title}</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Neden Bizi Tercih Etmelisiniz? — yeniden tasarım */}
        <div className="rounded-2xl bg-gradient-to-br from-orange-50 to-blue-50 p-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">
            Neden Bizi Tercih Etmelisiniz?
          </h3>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((f, i) => (
              <li
                key={i}
                className="flex items-start gap-3 rounded-xl bg-white/70 backdrop-blur border border-gray-200 p-4 hover:shadow-md transition"
              >
                <span className="mt-0.5">
                  <CheckCircle className="text-green-500" size={20} />
                </span>
                <span className="text-gray-700">{f}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Ekip */}
        <div className="mt-16 bg-gray-50 rounded-2xl p-8">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Uzman Ekibimiz</h3>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Alanında uzman, deneyimli ve sürekli gelişen bir ekiple yanınızdayız.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mx-auto mb-4">
                <Users size={32} />
              </div>
              <h4 className="font-semibold text-gray-900 mb-1">Gümrük Müşavirleri</h4>
              <p className="text-sm text-gray-600">Lisanslı ve deneyimli kadro</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-4">
                <Target size={32} />
              </div>
              <h4 className="font-semibold text-gray-900 mb-1">Operasyon Uzmanları</h4>
              <p className="text-sm text-gray-600">Uçtan uca süreç yönetimi</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center mx-auto mb-4">
                <Heart size={32} />
              </div>
              <h4 className="font-semibold text-gray-900 mb-1">Müşteri Temsilcileri</h4>
              <p className="text-sm text-gray-600">Proaktif iletişim ve destek</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
